import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Row, Col } from 'react-bootstrap';

import tickCircleGreen from "../../assets/images/common/tick-transparent.svg";

import InputBox from "../../components/ui/InputBox";
import Button from "../../components/ui/Button";
import CheckBox from "../../components/ui/CheckBox";

import LeftSection from "../../components/PreLogin/LoginPage/LeftSection.js";
import RightFooter from "../../components/PreLogin/RightFooter";

import { sideModalBackArrowIcon } from "../../utils/icons.js";

import "./index.scss";

import { useSelector, useDispatch } from 'react-redux';
import { registerUser, resetRegisterResponse } from '../../store/actions/registerAction.js';

import ReCAPTCHA from "react-google-recaptcha";

const googleRecaptchaSitekey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITEKEY;

const RegistrationPage = () => {

    const response = useSelector(state => state.register.registerResponse);

    const dispatch = useDispatch();
    const { search } = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [username, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [addRegStep, setAddRegStep] = useState(1);
    const [email, setEmail] = useState('');
    const [firstname, setFirstName] = useState('');
    const [lastname, setLastName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loginFailed, setLoginFailed] = useState(false);
    const [loginResponseErrorMsg, setLoginResponseErrorMsg] = useState(null);
    const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false);
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);

    const inputChange = (val, type) => {        
        const handlers = {
            text: setUserName,
            password: setPassword,
            firstname: setFirstName,
            lastname: setLastName,
            email: setEmail,
        };
        handlers[type] && handlers[type](val);
    };

    const buttonClicked = async () => {
        if (addRegStep === 1) {
            if (!username || !password) return;
            nextStep();
        } else if (addRegStep === 2) {
            if (!firstname || !lastname || !email) return;

            const formData = new FormData();
            formData.append('email', email);
            formData.append('username', username);
            formData.append('firstname', firstname);
            formData.append('lastname', lastname);
            formData.append('password', password);

            const queryParams = new URLSearchParams(search);
            const inviteToken = queryParams.get("invite_token");
            if(inviteToken !== null) {
                formData.append('invite_token', inviteToken);
            }
            setIsLoading(true);
            setLoginFailed(false);
            dispatch(registerUser(formData))
        }
    };


    const isDisabledButton = useCallback(() => {
        if (addRegStep === 1) {
            return !username || !password || !isRecaptchaVerified || !isTermsAccepted; // Add isTermsAccepted
        } else if (addRegStep === 2) {
            return !firstname || !lastname || !email;
        }
        return false;
    }, [addRegStep, username, password, firstname, lastname, email, isRecaptchaVerified, isTermsAccepted]);

    const nextStep = useCallback(() => {
        setAddRegStep((prevStep) => {
            if (prevStep < 4) {
                setIsRecaptchaVerified(false); // Reset reCAPTCHA status
                return prevStep + 1;
            }
            return 1;
        });
    }, []);


    const maskEmail = (email) => {
        const [name, domain] = email.split('@');
        if (!domain) return email;
        const maskedName = name.length <= 2 ? `${name[0]}****` : `${name[0]}****${name.slice(-1)}`;
        return `${maskedName}@${domain}`;
    };


    useEffect(() => {
        if (response && addRegStep === 2) {
            if (response?.detail?.status === "success") {
                setAddRegStep(3); // Move to confirmation step
                setIsLoading(false); // Stop the loader
            } else {
                setIsLoading(false); // Stop the loader
                setLoginFailed(true); // Show error message
            }
            setLoginResponseErrorMsg(response)
            dispatch(resetRegisterResponse())
        }
    }, [response, addRegStep, dispatch]);



    const prevStep = useCallback(() => {
        if(addRegStep === 1) {
            navigate("/auth/login")
        } else {
            setAddRegStep((prevStep) => (prevStep > 1 ? prevStep - 1 : prevStep));
        }
    }, [addRegStep, navigate]);


    return (
        <div className='register-container'>
            <Row className='h-100 m-0'>
                <Col md="6">
                    <LeftSection />
                </Col>
                <Col md="6">
                    <div className='register-right-section px-4 mx-3 h-100'>

                        <div className='right-container'>
                            {addRegStep <= 3 && (
                                <>
                                    <div className='mb-3'>
                                        <span onClick={prevStep} style={{ cursor: 'pointer' }}
                                        >{sideModalBackArrowIcon()}</span>
                                    </div>
                                    <div className="add-registration-step mb-3">
                                        {[1, 2, 3].map((step) => (
                                            <React.Fragment key={step}>
                                                <div
                                                    className={`d-flex align-items-center justify-content-center step-indicator ${addRegStep === step
                                                        ? 'inprogress'
                                                        : addRegStep > step
                                                            ? 'completed'
                                                            : ''
                                                        }`}
                                                >
                                                    {addRegStep < step ? step : ''}
                                                </div>
                                                {step < 3 && <div className="step-border"></div>}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </>

                            )}

                            {/* {loginFailed && <p className="error-msg text-center mb-2">{response?.detail?.description}</p>} */}
                            {loginFailed && (
                                <p className="error-msg text-center mb-2">
                                    {loginResponseErrorMsg?.description || "An error occurred. Please try again."}
                                </p>
                            )}

                            {addRegStep === 1 && (
                                <>
                                    <h1 className='mb-2'>{t("registrationPage.rightSection.account")}</h1>
                                    <p className='mb-3'>{t("registrationPage.rightSection.secondaryText")}</p>
                                    <div className='input-container mt-2'>
                                        <InputBox placeholder={'Enter username'} inputType={'text'} inputLabel={'User Name'} inputChange={inputChange} inputValue={username} />
                                        <InputBox placeholder={'Password'} inputType={'password'} inputLabel={'Password'} inputChange={inputChange} inputValue={password} />
                                    </div>
                                    <div className='loggedin-checkbox'>
                                        <label style={{ marginBottom: '5px' }}>Captcha</label>
                                        <ReCAPTCHA
                                            sitekey={googleRecaptchaSitekey}
                                            size="normal"
                                            onChange={() => setIsRecaptchaVerified(true)} // Update reCAPTCHA state
                                        />
                                    </div>

                                    <div className='d-flex justify-content-between mt-2'>
                                        <div className='loggedin-checkbox mb-2'>
                                            <CheckBox
                                                inputLabel={
                                                    <>
                                                        I agree to all the{' '}
                                                        <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                                                            Terms and Conditions
                                                        </a>
                                                    </>
                                                }
                                                index={0}
                                                checkBoxId={'0'}
                                                handleCheckboxChange={(e) => setIsTermsAccepted(e.target.checked)} // Update the state
                                                checkBoxValue={isTermsAccepted}
                                                disable={false}
                                            />
                                        </div>

                                    </div>
                                    <div className='submit-button mb-1'>
                                        <Button
                                            buttonText="Proceed"
                                            buttonType="primary"
                                            buttonClicked={buttonClicked}
                                            disableButton={isDisabledButton() || isLoading}
                                        />
                                    </div>
                                    <div className='not-register mb-3 mt-3'>
                                        <div>
                                            <p>Didn't receive the email Link Yet? <Link to="/auth/resend">Resend Link</Link></p>
                                        </div>
                                    </div>
                                </>
                            )}

                            {addRegStep === 2 && (
                                <>
                                    <div>
                                        <h1 className='mb-2'>{t("registrationPage.confirmation.mailId")}</h1>
                                        <p className='mb-3'>{t("registrationPage.confirmation.secondaryText")}</p>
                                        <div className='input-container mt-3'>
                                            <InputBox placeholder={'Enter First Name'} inputType={'firstname'} inputLabel={'First Name'} inputChange={inputChange} inputValue={firstname} />
                                            <InputBox placeholder={'Enter Last Name'} inputType={'lastname'} inputLabel={'Last Name'} inputChange={inputChange} inputValue={lastname} />
                                            <InputBox placeholder={'Enter Email address'} inputType={'email'} inputLabel={'Email Address'} inputChange={inputChange} inputValue={email} />
                                        </div>
                                        <div className='d-flex justify-content-between mt-3'>
                                            <div className='loggedin-checkbox mb-3'>
                                                <label>A confirmation link will be sent to the above mail ID</label>
                                            </div>
                                        </div>
                                        <div className='submit-button'>
                                            <Button buttonText={"Proceed"} buttonType="primary" buttonClicked={buttonClicked} isLoading={isLoading} />
                                        </div>
                                    </div>
                                </>
                            )}

                            {addRegStep === 3 && (
                                <div className="confirmation-success d-flex flex-column align-items-left text-left mt-4">
                                    <div className="tick-icon mb-3">
                                        <img src={tickCircleGreen} alt="confirmation tick" />
                                    </div>
                                    <h2 className="mb-3">{t("registrationPage.confirmation.successMessage")}</h2>
                                    <p>{`Account confirmation link has been sent successfully to your registered mail ID - ${maskEmail(email)}`}</p>
                                    <p className='mt-4'>Didn't receive the email Link Yet? <Link to="/auth/resend">Resend Link</Link></p>
                                </div>
                            )}
                        </div>
                        <RightFooter />
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default RegistrationPage;
